<script setup lang="ts">
import { computed, nextTick, useSlots, useTemplateRef } from 'vue';
import CloseIcon from '@/assets/icons/shared/close.svg';
import LockIcon from '@/assets/icons/core/lock.svg';
import { useFocusControl } from '@/modules/ui/composables/use-focus-control.ts';
import { useMaxLength } from '@/modules/ui/composables/use-max-length.ts';
import type { MaskInputOptions } from 'maska';
import { vMaska } from 'maska/vue';
import { getReadableMaskPlaceholder } from '@/modules/common/utils/to-input-mask.ts';
import UiButtonIcon from '@/modules/ui/components/ui/UiButtonIcon.vue';
import {
  deleteMaskedSlots,
  getCaretIndexFromClientX,
  getEditableCaretPositions,
  getMaskSlotFlags,
  moveCaretPosition,
  replaceMaskedSlots,
  snapCaretPosition,
} from '@/modules/ui/utils/mask-caret.util.ts';

export interface UiInputEmits {
  clear: [];
  keydown: [event: KeyboardEvent];
  focus: [event: FocusEvent];
  blur: [event: FocusEvent];
}

const model = defineModel<string>({ default: '' });

const {
  id,
  color = 'gray',
  size = 'm',
  placeholder = 'ui.common.placeholder.default',
  type = 'text',
  align = 'left',
  inputmode = 'text',
  autocomplete,
  autocapitalize,
  name,
  mask,
  maskPlaceholder,
  noClear,
  maxLength,
  error,
  disabled,
  readonly,
} = defineProps<{
  id?: string;
  error?: boolean;
  placeholder?: string;
  readonly?: boolean | 'button';
  disabled?: boolean;
  color?: 'gray' | 'white';
  size?: 'm' | 'l';
  type?: 'text' | 'number' | 'password';
  align?: 'left' | 'center';
  inputmode?: 'text' | 'search' | 'numeric';
  autocomplete?: string;
  autocapitalize?: 'none' | 'sentences' | 'words' | 'characters';
  name?: string;
  mask?: MaskInputOptions | string;
  maskPlaceholder?: string;
  noClear?: boolean;
  maxLength?: number;
  focusOnMount?: boolean;
}>();

const emit = defineEmits<UiInputEmits>();

const inputRef = useTemplateRef('input');

const isEditMask = computed(() => !!mask && type !== 'number');

const { isFocused, onFieldMousedown, onFieldClick, onFocus, onBlur } = useFocusControl(model, inputRef, emit, {
  trim: () => !isEditMask.value,
});

const { gradient, onInput } = useMaxLength(
  model,
  computed(() => maxLength),
  computed(() => type === 'number')
);

const slots = useSlots();

const showClear = computed(() => !noClear && !readonly && !disabled && model.value);

const showLock = computed(() => !!readonly && readonly !== 'button');

const resolvedMaskPlaceholder = computed(() => {
  if (!isEditMask.value) {
    return undefined;
  }

  return maskPlaceholder ?? getReadableMaskPlaceholder(mask);
});

const maskGuideRest = computed(() => {
  if (!resolvedMaskPlaceholder.value) {
    return '';
  }

  if (!model.value) {
    return isFocused.value ? resolvedMaskPlaceholder.value : '';
  }

  if (model.value.length >= resolvedMaskPlaceholder.value.length) {
    return '';
  }

  return resolvedMaskPlaceholder.value.slice(model.value.length);
});

const resolvedPlaceholder = computed(() => {
  if (resolvedMaskPlaceholder.value && isFocused.value && !model.value) {
    return '';
  }

  return placeholder;
});

const className = computed(() => [
  {
    _prefix: !!slots.prefix,
    _postfix: !!slots.postfix || showClear.value,
    _focused: isFocused.value,
    _disabled: disabled,
    _readonly: !!readonly,
    _button: readonly === 'button',
    _error: error,
  },
  `_size-${size}`,
  `_color-${color}`,
]);

function getEditablePositions(value: string) {
  return getEditableCaretPositions(getMaskSlotFlags(mask!, value));
}

function setMaskedValue(value: string, caret: number) {
  model.value = value;
  void nextTick(() => {
    inputRef.value?.setSelectionRange(caret, caret);
  });
}

function snapSelection() {
  const input = inputRef.value;

  if (!isEditMask.value || !input || input.selectionStart == null || input.selectionEnd == null) {
    return;
  }

  const positions = getEditablePositions(input.value);
  const start = snapCaretPosition(input.selectionStart, positions);
  const end = snapCaretPosition(input.selectionEnd, positions);

  if (start !== input.selectionStart || end !== input.selectionEnd) {
    input.setSelectionRange(start, end);
  }
}

function onClear() {
  model.value = '';
  emit('clear');
}

function onInputFocus(event: FocusEvent) {
  onFocus(event);

  if (isEditMask.value) {
    void nextTick(snapSelection);
  }
}

function onInputMousedown(event: MouseEvent) {
  if (!isEditMask.value || event.button !== 0 || event.shiftKey || readonly || disabled) {
    return;
  }

  const input = inputRef.value;

  if (!input) {
    return;
  }

  const raw = getCaretIndexFromClientX(input, event.clientX);
  const snapped = snapCaretPosition(raw, getEditablePositions(input.value));

  if (snapped === raw) {
    return;
  }

  event.preventDefault();

  if (document.activeElement !== input) {
    input.focus({ preventScroll: true });
  }

  input.setSelectionRange(snapped, snapped);
}

function onInputClick(event: MouseEvent) {
  onFieldClick(event);

  if (isEditMask.value) {
    snapSelection();
  }
}

function onKeydown(event: KeyboardEvent) {
  emit('keydown', event);

  if (
    !isEditMask.value ||
    event.defaultPrevented ||
    event.altKey ||
    event.ctrlKey ||
    event.metaKey ||
    readonly ||
    disabled
  ) {
    return;
  }

  const input = inputRef.value;

  if (!input || input.selectionStart == null || input.selectionEnd == null) {
    return;
  }

  const start = input.selectionStart;
  const end = input.selectionEnd;
  const positions = getEditablePositions(input.value);

  if (event.key === 'ArrowRight' && !event.shiftKey) {
    event.preventDefault();
    const next = moveCaretPosition(start, 1, positions);
    input.setSelectionRange(next, next);
    return;
  }

  if (event.key === 'ArrowLeft' && !event.shiftKey) {
    event.preventDefault();
    const next = moveCaretPosition(start, -1, positions);
    input.setSelectionRange(next, next);
    return;
  }

  if (event.key.length === 1) {
    event.preventDefault();
    const next = replaceMaskedSlots(mask!, input.value, start, end, event.key);
    setMaskedValue(next.value, next.caret);
    return;
  }

  if (event.key === 'Backspace') {
    event.preventDefault();
    const next = deleteMaskedSlots(mask!, input.value, start, end, 'backward');
    setMaskedValue(next.value, next.caret);
    return;
  }

  if (event.key === 'Delete') {
    event.preventDefault();
    const next = deleteMaskedSlots(mask!, input.value, start, end, 'forward');
    setMaskedValue(next.value, next.caret);
  }
}

function onPaste(event: ClipboardEvent) {
  if (!isEditMask.value || readonly || disabled) {
    return;
  }

  const input = inputRef.value;
  const text = event.clipboardData?.getData('text');

  if (!input || input.selectionStart == null || input.selectionEnd == null || text == null) {
    return;
  }

  event.preventDefault();
  const next = replaceMaskedSlots(mask!, input.value, input.selectionStart, input.selectionEnd, text);
  setMaskedValue(next.value, next.caret);
}

function onCut(event: ClipboardEvent) {
  if (!isEditMask.value || readonly || disabled) {
    return;
  }

  const input = inputRef.value;

  if (
    !input ||
    input.selectionStart == null ||
    input.selectionEnd == null ||
    input.selectionStart === input.selectionEnd
  ) {
    return;
  }

  event.preventDefault();
  event.clipboardData?.setData('text/plain', input.value.slice(input.selectionStart, input.selectionEnd));
  const next = deleteMaskedSlots(mask!, input.value, input.selectionStart, input.selectionEnd, 'backward');
  setMaskedValue(next.value, next.caret);
}

defineExpose({
  isFocused,
  focus: () => inputRef.value?.focus(),
  blur: () => inputRef.value?.blur(),
});
</script>

<template>
  <div
    ref="fieldRef"
    class="ui-input"
    :class="className"
    @mousedown="onFieldMousedown"
    @click="onInputClick"
  >
    <div class="content">
      <slot name="prefix" />

      <div
        class="input-wrap"
        :class="{ _masked: !!resolvedMaskPlaceholder }"
      >
        <span
          v-if="maskGuideRest"
          class="mask-guide"
          :class="`_align-${align}`"
          aria-hidden="true"
        >
          <span class="mask-guide__value">{{ model }}</span>
          <span class="mask-guide__rest">{{ maskGuideRest }}</span>
        </span>

        <input
          :id
          ref="input"
          v-maska="mask"
          v-focus="focusOnMount"
          class="input"
          :class="`_align-${align}`"
          :type="type === 'password' ? 'password' : undefined"
          :name
          :value="model"
          :placeholder="$maybeT(resolvedPlaceholder)"
          :inputmode
          :autocomplete
          :autocapitalize
          :maxlength="type === 'number' ? undefined : maxLength"
          :disabled
          :readonly="!!readonly"
          data-1p-ignore="true"
          data-lpignore="true"
          data-form-type="other"
          :data-maska-number="type === 'number' ? '' : null"
          :data-maska-number-locale="type === 'number' ? 'ru' : null"
          @maska="onInput"
          @focus="onInputFocus"
          @blur="onBlur"
          @mousedown="onInputMousedown"
          @select="snapSelection"
          @keydown="onKeydown"
          @paste="onPaste"
          @cut="onCut"
        />
      </div>

      <UiButtonIcon
        v-if="showClear"
        color="gray"
        tabindex="-1"
        :error
        @mousedown.stop.prevent
        @click.stop.prevent="onClear"
      >
        <CloseIcon />
      </UiButtonIcon>

      <slot name="postfix" />

      <span
        v-if="showLock"
        class="lock"
      >
        <LockIcon class="lock-icon" />
      </span>
    </div>
  </div>
</template>

<style scoped lang="scss">
.ui-input {
  min-width: 0;
  border-radius: 8px;
  cursor: text;
  background: linear-gradient(
    to right,
    rgb(226 231 239 / 100%) 0%,
    rgb(226 231 239 / 100%) v-bind('gradient.start'),
    transparent v-bind('gradient.end'),
    transparent 100%
  );
  transition:
    box-shadow var(--transition-fast),
    background-color var(--transition-fast);

  &._size-m {
    height: 36px;
    padding: 0 12px;

    &._prefix {
      padding-left: 8px;

      .input,
      .mask-guide {
        padding-left: 4px;
      }
    }

    &._postfix {
      padding-right: 8px;

      .input,
      .mask-guide {
        padding-right: 4px;
      }
    }

    .input,
    .mask-guide {
      font-size: var(--font-size-l);
      line-height: var(--font-size-l);
    }
  }

  &._size-l {
    height: 48px;
    padding: 0 16px;

    &._prefix {
      padding-left: 16px;

      .input,
      .mask-guide {
        padding-left: 12px;
      }
    }

    &._postfix {
      padding-right: 16px;

      .input,
      .mask-guide {
        padding-right: 12px;
      }
    }

    .input,
    .mask-guide {
      font-size: var(--font-size-xl);
      line-height: var(--font-size-xl);
    }
  }

  &._color-gray {
    background-color: var(--color-gray-075);

    &._focused:not(._error, ._disabled, ._button) {
      box-shadow: var(--shadow-control);
    }
  }

  &._color-white {
    background-color: var(--color-white);
    box-shadow: var(--shadow);
  }

  &._disabled {
    .content {
      opacity: 0.5;
    }
  }

  &._readonly {
    &:not(._button) {
      padding-right: 12px;
      background: var(--color-gray-050);
      box-shadow: none;
    }

    &._button {
      .content {
        opacity: 0.5;
      }
    }
  }

  &._button {
    cursor: pointer;

    .input {
      cursor: pointer;
    }
  }

  &._error {
    background: var(--color-red-100)
      linear-gradient(
        to right,
        var(--color-red-250) 0%,
        var(--color-red-250) v-bind('gradient.start'),
        transparent v-bind('gradient.end'),
        transparent 100%
      );

    &._disabled,
    &._readonly {
      background-image: none;
      background-color: var(--color-red-100);
    }

    &._focused {
      box-shadow: var(--shadow-control-error);
    }

    .input {
      color: var(--color-red-800);

      &::placeholder {
        color: var(--color-red-800);
      }

      &:-webkit-autofill {
        -webkit-text-fill-color: var(--color-red-800) !important;
      }
    }

    .mask-guide__rest {
      color: var(--color-red-800);
      opacity: 0.5;
    }
  }
}

.content {
  display: flex;
  height: 100%;
  min-width: 0;
  align-items: center;
}

.input-wrap {
  display: flex;
  width: 100%;
  min-width: 0;
  flex: 1 1 auto;
  align-self: stretch;

  .input {
    width: 100%;
  }

  &._masked {
    position: relative;

    .input {
      position: relative;
      z-index: 1;
    }
  }
}

.mask-guide {
  position: absolute;
  display: flex;
  inset: 0;
  align-items: center;
  box-sizing: border-box;
  overflow: hidden;
  pointer-events: none;
  white-space: pre;
  font-family: inherit;
  font-weight: inherit;
  font-size: inherit;
  line-height: inherit;

  &._align-center {
    justify-content: center;
  }
}

.mask-guide__value {
  color: transparent;
}

.mask-guide__rest {
  color: var(--color-gray-500);
}

.lock {
  display: flex;
  flex-shrink: 0;
  margin-left: 8px;
  align-items: center;
}

.lock-icon {
  width: 16px;
  height: 16px;
  fill: var(--color-gray-600);
}

.input {
  min-width: 0;
  flex-grow: 1;
  align-self: stretch;
  appearance: textfield;
  transition: color var(--transition-fast);

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    appearance: none;
  }

  &::placeholder {
    transition: color var(--transition-fast);
  }

  &._align-left {
    text-align: left;
  }

  &._align-center {
    text-align: center;
  }

  &:-webkit-autofill {
    transition: background-color 0s 9999s;
    -webkit-text-fill-color: var(--color-gray-1000) !important;
  }
}
</style>

<style lang="scss">
.ui-input .input::-webkit-contacts-auto-fill-button,
.ui-input .input::-webkit-credentials-auto-fill-button,
.ui-input .input::-webkit-caps-lock-indicator {
  position: absolute !important;
  right: 0 !important;
  display: none !important;
  width: 0 !important;
  height: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  background: transparent !important;
  opacity: 0 !important;
  pointer-events: none !important;
  visibility: hidden !important;
  appearance: none !important;
}
</style>
